import React, {
  useCallback,
  useEffect,
  useState,
} from 'react';
import { useSelector } from 'react-redux';
import isNil from 'lodash/isNil';
import { PopulateUIProvider } from '@populateui/core';
import {
  Questionnaire,
  QuestionnaireAnswer,
  QuestionnaireQuestion,
} from '@populateui/ui';
import CompletionMessage from 'components/common/CompletionMessage';
import HeaderLogo from 'components/common/headerLogo';
import QuestionnairePageHeader, {
  fixedQuestionnairePageClasses,
} from 'components/common/QuestionnairePageHeader';
import RtmEnrollment from 'components/rtmEnrollment';
import Spinner from 'components/spinner/Spinner';
import useCommuniqueState from 'hooks/useCommuniqueState';
import useIncompleteRtmRedirect from 'hooks/useIncompleteRtmRedirect';
import useQuestionnaireScrollCompensation from 'hooks/useQuestionnaireScrollCompensation';
import {
  useGetRTMCheckInQuestions,
  useSubmitRTMCheckIn,
} from 'hooks/useApi';
import { RootState } from 'store/store';
import { ModuleTypes } from 'types/appState';
import {
  isEmptyString,
  parseErrorMessage,
} from 'utils/helper';
import { isTabletBaseIntake, patientPortalLoginUrl } from 'env';

const RTM_BRAND = {
  brand: {
    primary: '#54b3a7',
    primaryInvert: '#ffffff',
    primaryHover: '#4aa89c',
    primaryActive: '#439e92',
  },
};

type RtmCheckInProps = {
  onDisplayChooseScreen?: () => void;
};

function RtmCheckIn({ onDisplayChooseScreen }: RtmCheckInProps) {
  const {
    rtmToken,
    rtmKind,
    appointmentToken,
    name: appModuleName,
  } = useSelector((state: RootState) => state.appStateSettings);
  const isTablet = isTabletBaseIntake || !isNil(appointmentToken);
  const {
    data: checkInQuestionsData,
    isLoading,
    isError,
    error,
    refetch,
  } = useGetRTMCheckInQuestions(rtmToken ?? '', !!rtmToken);
  const {
    mutateAsync: submitRTMCheckIn,
    isPending: isSubmitting,
  } = useSubmitRTMCheckIn(rtmToken ?? '');
  const { goToCommuniqueChoice } = useIncompleteRtmRedirect();
  const { communiqueData, setCommuniqueData } = useCommuniqueState();
  const [showCompletedMessage, setShowCompletedMessage] = useState(false);
  const [answeredCount, setAnsweredCount] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [hasCompletedEnrollment, setHasCompletedEnrollment] = useState(false);

  const handleEnrollmentComplete = useCallback(() => {
    setHasCompletedEnrollment(true);
    refetch();
  }, [refetch]);

  const handleSubmit = async (answers: QuestionnaireAnswer[]) => {
    try {
      await submitRTMCheckIn({
        token: rtmToken,
        answers: answers.map((answer) => ({
          questionId: answer.questionId,
          value: answer.value,
        })),
      });

      if (!isNil(communiqueData)) {
        setCommuniqueData({
          ...communiqueData,
          rtm: {
            ...communiqueData.rtm,
            hasCompleted: true,
            kind: 'checkIn',
          },
        });
      }

      if (onDisplayChooseScreen) {
        onDisplayChooseScreen();
      } else if (!isNil(communiqueData) || isTablet) {
        goToCommuniqueChoice();
      } else {
        setShowCompletedMessage(true);
      }
    } catch (error) {
      console.error('Error submitting check-in answers:', error);
    }
  };

  const isExplicitCheckIn = (rtmKind?.toString() ?? '').toLowerCase().includes('checkin')
    || (communiqueData?.rtm?.kind?.toString() ?? '').toLowerCase().includes('checkin')
    || (
      appModuleName === ModuleTypes.rtmCheckIn
      && (rtmKind?.toString() ?? '').toLowerCase() !== 'enrollment'
    );

  const questions = (checkInQuestionsData?.questions ?? []) as QuestionnaireQuestion[];
  const enrollmentToken = checkInQuestionsData?.enrollmentToken ?? '';
  const needsEnrollment = !isExplicitCheckIn
    && checkInQuestionsData?.isEnrollmentPending === true
    && !hasCompletedEnrollment;
  const shouldShowEnrollmentGate = !isLoading
    && !isError
    && !showCompletedMessage
    && needsEnrollment
    && !isEmptyString(enrollmentToken);
  const showError = !isLoading
    && !shouldShowEnrollmentGate
    && (isError
      || (!showCompletedMessage && questions.length === 0)
      || (needsEnrollment && isEmptyString(enrollmentToken)));
  const showQuestionnaire = !isLoading
    && !showCompletedMessage
    && !shouldShowEnrollmentGate
    && !needsEnrollment
    && questions.length > 0;
  const rtmQuestionsErrorMessage = isError
    ? parseErrorMessage(error, 'Error loading check-in questions')
    : 'Error loading check-in questions';

  useQuestionnaireScrollCompensation(showQuestionnaire);

  useEffect(() => {
    setAnsweredCount(0);
    setTotalCount(questions.length);
  }, [questions.length]);

  useEffect(() => {
    document.documentElement.classList.add('rtm-page-background');
    document.body.classList.add('rtm-page-background');

    return () => {
      document.documentElement.classList.remove('rtm-page-background');
      document.body.classList.remove('rtm-page-background');
    };
  }, []);

  // useEffect(() => {
  //   if (!isLoading && !isError && questions.length === 0 && !showCompletedMessage) {
  //     if (onDisplayChooseScreen) {
  //       onDisplayChooseScreen();
  //     } else if (!isNil(communiqueData) || isTabletBaseIntake) {
  //       goToCommuniqueChoice();
  //     }
  //   }
  // }, [
  //   communiqueData,
  //   goToCommuniqueChoice,
  //   isError,
  //   isLoading,
  //   isTabletBaseIntake,
  //   onDisplayChooseScreen,
  //   questions.length,
  //   showCompletedMessage,
  // ]);

  const containerClassName = showQuestionnaire
    ? 'min-h-screen'
    : 'min-h-screen py-8';

  return shouldShowEnrollmentGate ? (
    <RtmEnrollment
      tokenOverride={enrollmentToken}
      onEnrollmentComplete={handleEnrollmentComplete}
    />
  ) : (
    <PopulateUIProvider config={RTM_BRAND}>
      <div className={containerClassName}>
        {!showQuestionnaire && <HeaderLogo />}
        {isLoading ? (
          <div className="flex justify-center items-center h-[50vh]">
            <Spinner />
          </div>
        ) : null}
        {showError ? (
          <div className="flex justify-center items-center h-[50vh]">
            <p className="text-red-600">{rtmQuestionsErrorMessage}</p>
          </div>
        ) : null}
        {!isLoading && showCompletedMessage ? (
          <CompletionMessage
            title="Check-in Complete"
            message="Thank you. Your care team will review your responses."
            actionLabel="Go to patient portal"
            actionHref={patientPortalLoginUrl}
          />
        ) : null}
        {showQuestionnaire ? (
          <>
            <QuestionnairePageHeader
              eyebrow="Treatment Progress Check-In Survey"
              answeredCount={answeredCount}
              totalCount={totalCount}
            />
            <Questionnaire
              questions={questions}
              onSubmit={handleSubmit}
              isSubmitting={isSubmitting}
              onAnswersChange={({ answeredCount: count, totalCount: total }) => {
                setAnsweredCount(count);
                setTotalCount(total);
              }}
              submitLabel="SUBMIT"
              classes={fixedQuestionnairePageClasses}
            />
          </>
        ) : null}
      </div>
    </PopulateUIProvider>
  );
}

export default RtmCheckIn;
